import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * HIPAA §164.312(a)(2)(iii): Automatic logoff.
 * Logs the user out after INACTIVE_MS of no interaction (default 15 min).
 * Shows a 1-minute warning before logout.
 */
const INACTIVE_MS = 15 * 60 * 1000;
const WARN_BEFORE_MS = 60 * 1000;

export function useIdleLogout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);

  useEffect(() => {
    if (!user) return;

    const reset = () => {
      if (warnTimer.current) clearTimeout(warnTimer.current);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      warnTimer.current = setTimeout(() => {
        toast.warning("Session ends in 1 minute due to inactivity", { duration: 5000 });
      }, INACTIVE_MS - WARN_BEFORE_MS);
      logoutTimer.current = setTimeout(async () => {
        toast.error("Signed out for inactivity");
        await logout();
        nav("/auth");
      }, INACTIVE_MS);
    };

    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (warnTimer.current) clearTimeout(warnTimer.current);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
    };
  }, [user, logout, nav]);
}
